/**
 * 에셋 배달 토큰 조달 — PLAN_ASSET_DELIVERY_HARDENING.md Phase 1.
 *
 * BE `POST /api/v1/assets/token` (permitAll) 이 RS256 토큰을 준다. 이 토큰이 있어야
 * manifest·keys/unwrap 과 파일 다운로드가 통과한다.
 *
 * ⚠ **호출부는 토큰을 모른다.** 조달·캐시·갱신이 전부 여기 있고 `configureAssetDelivery`
 * 만 이 모듈을 쓴다. 종전 설계는 `main.tsx` 와 `applyScanLanguage` 두 곳에서 각각 토큰을
 * 받아 넘기는 형태였는데, 그러면 같은 캐시·재시도 코드가 두 벌이 된다.
 *
 * ⚠ **선제 갱신을 하는 이유**: asset-delivery 플러그인은 `authToken` 을 configure 시점에
 * 고정하고 갱신 콜백이 없다. sync 도중 만료되면 `Downloader` 가 일반 오류로 떨어지고
 * (401/403 을 구분하지 않는다) 그 sync 는 실패한다. 첫 설치가 66MB 라 이게 실재하는
 * 시나리오다 — 그래서 서버 TTL 은 24h 이고, 여기서 잔여가 임계 미만이면 미리 새로 받는다.
 */
import { apiPost, apiRootUrl } from "../api/client";
import { getAttestation } from "./appAttest";
import { isNative } from "./index";

interface AssetTokenResponse {
  token: string;
  /** 초 */
  expiresIn: number;
  kid: string;
}

interface CachedToken {
  token: string;
  /** epoch ms — 이 시각 이후로는 쓰지 않는다 */
  expiresAt: number;
  kid: string;
}

/**
 * 잔여가 이 값 미만이면 캐시를 버리고 새로 받는다.
 *
 * ⚠ sync 한 번이 이 시간 안에 끝난다는 뜻이 아니라, **sync 시작 시점에 최소 이만큼은
 * 남아 있게** 한다는 뜻이다. 서버 TTL 24h 에 이 임계 1h 면 실질 유효창이 23h 다.
 */
const REFRESH_THRESHOLD_MS = 60 * 60 * 1000;

let cached: CachedToken | null = null;
/** 동시 호출이 토큰을 여러 번 받지 않게 진행 중 요청에 합류시킨다. */
let inFlight: Promise<CachedToken | null> | null = null;

/**
 * 무결성 증명을 붙여 본다 — PLAN_ASSET_DELIVERY_HARDENING.md Phase 4.
 *
 * ⚠ **네이티브가 아니면 challenge 조차 받지 않는다.** 웹은 증명을 만들 수단이 없으므로
 * 물어볼 이유가 없다 — 안 그러면 데스크탑 웹이 부팅마다 쓸모없는 왕복을 한 번 더 한다.
 *
 * 네이티브에서는 BE 모드를 모른 채 일단 시도한다. `mode` 를 보고 분기하려면 그걸 알기 위한
 * 왕복이 어차피 필요하고, 앱 실행당 1회(토큰이 세션 캐시된다)라 비용이 작다. 대신 BE 가
 * SHADOW 로 올리는 순간 **앱 배포 없이** 관측이 시작된다 — SHADOW 를 둔 이유가 그것이다.
 *
 * ⚠ 증명을 못 만들어도 **토큰 발급은 계속 진행한다**(정상 실패가 많다 — 시뮬레이터,
 * dev 플레이버, Play services 없는 기기, 웹). 막을지 말지는 BE 가 정한다.
 */
async function attestBody(): Promise<Record<string, string> | null> {
  if (!isNative()) return null;
  try {
    const ch = await apiPost<{ challenge: string; mode: string } | null>(
      `${apiRootUrl()}/assets/attest/challenge`,
      {},
    );
    if (!ch?.challenge) return null;
    const att = await getAttestation(ch.challenge);
    if (!att) return null;
    return { platform: att.platform, challenge: ch.challenge, attestation: att.attestation };
  } catch {
    return null;
  }
}

async function request(): Promise<CachedToken | null> {
  try {
    // ⚠ client baseURL 은 …/api/v1/user 다 — 상대경로로 넘기면 /user 가 중복돼
    //   permitAll 을 벗어난다(2026-08-04 실사고, api/asset.ts 주석 참조).
    const res = await apiPost<AssetTokenResponse | null>(
      `${apiRootUrl()}/assets/token`,
      (await attestBody()) ?? {},
    );
    if (!res?.token || typeof res.expiresIn !== "number") return null;
    return {
      token: res.token,
      expiresAt: Date.now() + res.expiresIn * 1000,
      kid: res.kid,
    };
  } catch {
    // 실패를 삼키고 null 을 준다 — 호출부(configureAssetDelivery)가 **토큰 없이 진행**한다.
    // 이미 받아둔 에셋의 lease 는 configure 와 무관하므로 오프라인에서도 스캔은 살아 있다.
    return null;
  }
}

/**
 * 유효한 토큰을 돌려준다. 캐시가 살아 있으면 재사용, 만료 임박이면 새로 받는다.
 * 실패 시 null — 호출부는 토큰 없이 진행해야 한다(막지 말 것).
 */
export async function getAssetToken(): Promise<string | null> {
  if (cached && cached.expiresAt - Date.now() > REFRESH_THRESHOLD_MS) {
    return cached.token;
  }
  if (!inFlight) {
    inFlight = request().finally(() => {
      inFlight = null;
    });
  }
  const fresh = await inFlight;
  if (fresh) cached = fresh;
  // 갱신에 실패해도 아직 안 만료된 캐시가 있으면 그거라도 쓴다 —
  // 임계(1h)는 여유분이지 만료가 아니다.
  if (!fresh && cached && cached.expiresAt > Date.now()) return cached.token;
  return fresh?.token ?? null;
}

/** 디버그 메뉴 표시용 — 현재 토큰 상태. 토큰 본문은 노출하지 않는다. */
export function assetTokenStatus(): { kid: string; expiresAt: number } | null {
  return cached ? { kid: cached.kid, expiresAt: cached.expiresAt } : null;
}

/** 테스트 전용 — 모듈 캐시 초기화. */
export function __resetAssetTokenCache(): void {
  cached = null;
  inFlight = null;
}
